import React, { useEffect, useState, Component } from 'react';
import { ArrowLeft, Plus, Check, Clock, MapPin, Users, ThumbsUp, ThumbsDown, Share2, Coffee, Calendar as CalendarIcon, Search, User, X } from 'lucide-react';
import { Plan, Store, PlanMember } from '../../types';
import { MOCK_PLANS, STORES } from '../../data/mockData';
import { useToast } from '../ToastContext';
interface PlanScreenProps {
  onBack: () => void;
  onNavigate: (screen: string, data?: any) => void;
}
type ViewState = 'list' | 'create' | 'detail';
type CreateStep = 1 | 2 | 3;
export function PlanScreen({
  onBack,
  onNavigate
}: PlanScreenProps) {
  const [view, setView] = useState<ViewState>('list');
  const [plans, setPlans] = useState<Plan[]>(MOCK_PLANS);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  // Create Plan State
  const [createStep, setCreateStep] = useState<CreateStep>(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [newPlanStore, setNewPlanStore] = useState<Store | null>(null);
  const [newPlanDate, setNewPlanDate] = useState<string>('');
  const [newPlanTime, setNewPlanTime] = useState<string>('');
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const {
    showToast
  } = useToast();
  const activePlans = plans.filter(p => p.status === 'voting' || p.status === 'locked');
  const pastPlans = plans.filter(p => p.status === 'completed' || p.status === 'expired');
  const selectedPlan = plans.find(p => p.id === selectedPlanId);
  const mockContacts = [{
    id: 'c1',
    name: 'Emma Thompson',
    phone: '+1 (555) 123-4567'
  }, {
    id: 'c2',
    name: 'James Wilson',
    phone: '+1 (555) 234-5678'
  }, {
    id: 'c3',
    name: 'Sophia Chen',
    phone: '+1 (555) 345-6789'
  }, {
    id: 'c4',
    name: 'Marcus Johnson',
    phone: '+1 (555) 456-7890'
  }, {
    id: 'c5',
    name: 'Priya Patel',
    phone: '+1 (555) 567-8901'
  }];
  const filteredStores = STORES.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleCreatePlan = () => {
    if (!newPlanStore || !newPlanDate || !newPlanTime) return;
    // Combine date and time
    const dateTimeStr = `${newPlanDate}T${newPlanTime}:00`;
    const dateTime = new Date(dateTimeStr);
    const members: PlanMember[] = [{
      id: 'me',
      name: 'You',
      status: 'confirmed',
      isHost: true
    }, ...selectedContacts.map(id => {
      const contact = mockContacts.find(c => c.id === id);
      return {
        id,
        name: contact?.name || 'Friend',
        status: 'pending' as const,
        isHost: false
      };
    })];
    const newPlan: Plan = {
      id: `plan-${Date.now()}`,
      title: `${newPlanStore.tags[0] || 'Hangout'} Plan ☕`,
      storeId: newPlanStore.id,
      storeName: newPlanStore.name,
      storeImage: newPlanStore.image,
      dateTime: dateTime.toISOString(),
      status: 'voting',
      suggestionsRemaining: 3,
      createdAt: new Date().toISOString(),
      members,
      suggestions: []
    };
    setPlans([newPlan, ...plans]);
    setSelectedPlanId(newPlan.id);
    setView('detail');
    showToast('Plan created successfully!', 'success');
    // Reset create state
    setCreateStep(1);
    setNewPlanStore(null);
    setNewPlanDate('');
    setNewPlanTime('');
    setSelectedContacts([]);
  };
  const handleShareWhatsApp = (plan: Plan) => {
    const date = new Date(plan.dateTime).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
    const message = `Hey! I've created a plan for ${plan.storeName} on ${date}. Join here: https://kraya.app/p/${plan.id}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };
  const handleUpdateStatus = (planId: string, status: 'confirmed' | 'maybe' | 'declined') => {
    setPlans(plans.map(p => {
      if (p.id === planId) {
        const updatedMembers = p.members.map(m => m.id === 'me' || m.name === 'Aryan' ? {
          ...m,
          status
        } : m);
        // Check if we hit 60% threshold
        const totalMembers = updatedMembers.length;
        const confirmedCount = updatedMembers.filter(m => m.status === 'confirmed').length;
        const threshold = Math.ceil(totalMembers * 0.6);
        const newStatus = p.status === 'voting' && confirmedCount >= threshold ? 'locked' : p.status;
        const groupPerk = newStatus === 'locked' && !p.groupPerk ? 'Free fries on arrival 🍟' : p.groupPerk;
        if (newStatus === 'locked' && p.status === 'voting') {
          showToast('🎉 Plan is locked! Group perk unlocked!', 'success');
        }
        return {
          ...p,
          members: updatedMembers,
          status: newStatus,
          groupPerk
        };
      }
      return p;
    }));
  };
  // Simulated friend responses
  const [simulationStarted, setSimulationStarted] = useState<string | null>(null);
  useEffect(() => {
    if (view !== 'detail' || !selectedPlanId) return;
    const plan = plans.find(p => p.id === selectedPlanId);
    if (!plan || plan.status !== 'voting' || simulationStarted === selectedPlanId) return;
    setSimulationStarted(selectedPlanId);
    // Find pending/maybe members to simulate responses
    const pendingMembers = plan.members.filter(m => m.status === 'pending' || m.status === 'maybe');
    const responses: Array<{
      memberId: string;
      status: 'confirmed' | 'maybe';
      delay: number;
    }> = [];
    pendingMembers.forEach((m, i) => {
      // Stagger responses: 2s, 5s, 8s...
      const delay = (i + 1) * 3000;
      // Most confirm, some maybe
      const status = i === pendingMembers.length - 1 && pendingMembers.length > 2 ? 'maybe' : 'confirmed';
      responses.push({
        memberId: m.id,
        status,
        delay
      });
    });
    const timers = responses.map(({
      memberId,
      status,
      delay
    }) => setTimeout(() => {
      setPlans(prev => prev.map(p => {
        if (p.id !== selectedPlanId) return p;
        const updatedMembers = p.members.map(m => m.id === memberId ? {
          ...m,
          status
        } : m);
        const totalMembers = updatedMembers.length;
        const confirmedCount = updatedMembers.filter(m => m.status === 'confirmed').length;
        const threshold = Math.ceil(totalMembers * 0.6);
        const shouldLock = p.status === 'voting' && confirmedCount >= threshold;
        const newStatus = shouldLock ? 'locked' : p.status;
        const groupPerk = shouldLock && !p.groupPerk ? 'Free fries on arrival 🍟' : p.groupPerk;
        const memberName = updatedMembers.find(m => m.id === memberId)?.name || 'Someone';
        if (status === 'confirmed') {
          showToast(`${memberName} confirmed! ✓`, 'success');
        } else {
          showToast(`${memberName} said maybe`, 'info');
        }
        if (shouldLock) {
          setTimeout(() => showToast("🎉 Plan is locked! Everyone's in!", 'success'), 500);
        }
        return {
          ...p,
          members: updatedMembers,
          status: newStatus,
          groupPerk
        };
      }));
    }, delay));
    return () => timers.forEach(clearTimeout);
  }, [view, selectedPlanId]);
  // Countdown Timer Hook
  const useCountdown = (targetDate: string) => {
    const [timeLeft, setTimeLeft] = useState('');
    const [isUrgent, setIsUrgent] = useState(false);
    const [isVeryUrgent, setIsVeryUrgent] = useState(false);
    useEffect(() => {
      const calculateTimeLeft = () => {
        const difference = new Date(targetDate).getTime() - new Date().getTime();
        if (difference <= 0) {
          setTimeLeft('Started');
          return;
        }
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor(difference / 1000 / 60 % 60);
        if (hours === 0 && minutes < 5) {
          setTimeLeft('Starting soon!');
          setIsVeryUrgent(true);
        } else if (hours === 0) {
          setTimeLeft(`Starts in ${minutes}m`);
          setIsUrgent(true);
        } else {
          setTimeLeft(`Starts in ${hours}h ${minutes}m`);
          setIsUrgent(false);
        }
      };
      calculateTimeLeft();
      const timer = setInterval(calculateTimeLeft, 60000);
      return () => clearInterval(timer);
    }, [targetDate]);
    return {
      timeLeft,
      isUrgent,
      isVeryUrgent
    };
  };
  const CountdownDisplay = ({
    dateStr
  }: {
    dateStr: string;
  }) => {
    const {
      timeLeft,
      isUrgent,
      isVeryUrgent
    } = useCountdown(dateStr);
    return <span className={`font-bold ${isVeryUrgent ? 'text-red-600' : isUrgent ? 'text-orange-600' : 'text-gray-600'}`} data-id="element-1086">
        ⏳ {timeLeft}
      </span>;
  };
  const renderList = () => <div className="pb-24 pt-6 px-5 animate-in fade-in duration-300 min-h-screen bg-gray-50" data-id="element-1087">
      <div className="flex items-center gap-4 mb-6" data-id="element-1088">
        <button onClick={onBack} className="w-10 h-10 bg-black rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-1089">
          <ArrowLeft size={20} className="text-[#FFFC00]" strokeWidth={2.5} data-id="element-1090" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1091">My Plans</h1>
      </div>

      <button onClick={() => setView('create')} className="w-full py-4 bg-[#FFFC00] text-black rounded-2xl font-bold text-lg active:scale-[0.98] transition-transform shadow-lg mb-8 flex items-center justify-center gap-2" data-id="element-1092">
        <Plus size={24} data-id="element-1093" />
        Create New Plan
      </button>

      {activePlans.length > 0 && <div className="mb-8" data-id="element-1094">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1095">Active Plans</h2>
          <div className="space-y-4" data-id="element-1096">
            {activePlans.map(plan => <button key={plan.id} onClick={() => {
          setSelectedPlanId(plan.id);
          setView('detail');
        }} className="w-full bg-white rounded-2xl p-4 text-left shadow-sm active:scale-[0.98] transition-transform border border-gray-100" data-id="element-1097">
                <div className="flex justify-between items-start mb-2" data-id="element-1098">
                  <h3 className="font-bold text-lg text-gray-900" data-id="element-1099">
                    {plan.title}
                  </h3>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${plan.status === 'locked' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-1100">
                    {plan.status === 'locked' ? 'Locked' : 'Voting'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1" data-id="element-1101">
                  <MapPin size={14} data-id="element-1102" /> {plan.storeName}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3" data-id="element-1103">
                  <Clock size={14} data-id="element-1104" />{' '}
                  {new Date(plan.dateTime).toLocaleString('en-US', {
              weekday: 'short',
              hour: 'numeric',
              minute: '2-digit'
            })}
                </div>
                <div className="flex items-center justify-between" data-id="element-1105">
                  <div className="flex -space-x-2" data-id="element-1106">
                    {plan.members.slice(0, 3).map((m, i) => <div key={i} className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs font-bold" data-id="element-1107">
                        {m.name.charAt(0)}
                      </div>)}
                    {plan.members.length > 3 && <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-bold text-gray-600" data-id="element-1108">
                        +{plan.members.length - 3}
                      </div>}
                  </div>
                  <CountdownDisplay dateStr={plan.dateTime} data-id="element-1109" />
                </div>
              </button>)}
          </div>
        </div>}

      {pastPlans.length > 0 && <div data-id="element-1110">
          <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1111">Past Plans</h2>
          <div className="space-y-4" data-id="element-1112">
            {pastPlans.map(plan => <button key={plan.id} onClick={() => {
          setSelectedPlanId(plan.id);
          setView('detail');
        }} className="w-full bg-white/60 rounded-2xl p-4 text-left shadow-sm active:scale-[0.98] transition-transform border border-gray-100 opacity-70" data-id="element-1113">
                <div className="flex justify-between items-start mb-2" data-id="element-1114">
                  <h3 className="font-bold text-lg text-gray-900" data-id="element-1115">
                    {plan.title}
                  </h3>
                  <span className="text-xs font-bold px-2 py-1 rounded-full bg-gray-200 text-gray-700" data-id="element-1116">
                    Completed
                  </span>
                </div>
                <div className="text-sm text-gray-600 mb-1" data-id="element-1117">
                  {plan.storeName}
                </div>
                <div className="text-sm text-gray-500" data-id="element-1118">
                  {new Date(plan.dateTime).toLocaleDateString()}
                </div>
              </button>)}
          </div>
        </div>}

      {plans.length === 0 && <div className="text-center py-12" data-id="element-1119">
          <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1120">
            <Coffee size={40} className="text-yellow-600" data-id="element-1121" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1122">No plans yet</h3>
          <p className="text-gray-500 mb-6" data-id="element-1123">
            Rally your friends and make something happen!
          </p>
        </div>}
    </div>;
  const renderCreate = () => <div className="pb-24 pt-6 px-5 animate-in fade-in slide-in-from-right duration-300 min-h-screen bg-[#FFFC00]" data-id="element-1124">
      <div className="flex items-center gap-4 mb-6" data-id="element-1125">
        <button onClick={() => {
        if (createStep > 1) setCreateStep(createStep - 1 as CreateStep);else setView('list');
      }} className="w-10 h-10 bg-black rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-1126">
          <ArrowLeft size={20} className="text-[#FFFC00]" strokeWidth={2.5} data-id="element-1127" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1128">Create Plan</h1>
      </div>

      <div className="flex justify-between mb-8 px-4" data-id="element-1129">
        {[1, 2, 3].map(step => <div key={step} className="flex flex-col items-center" data-id="element-1130">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mb-1 transition-colors ${step < createStep ? 'bg-black text-[#FFFC00]' : step === createStep ? 'bg-white text-black border-2 border-black' : 'bg-white/50 text-gray-500'}`} data-id="element-1131">
              {step < createStep ? <Check size={14} strokeWidth={3} data-id="element-1132" /> : step}
            </div>
            <span className="text-xs font-medium text-gray-700" data-id="element-1133">
              {step === 1 ? 'Place' : step === 2 ? 'Time' : 'Friends'}
            </span>
          </div>)}
      </div>

      <div className="bg-[#FFF9C4] rounded-3xl p-6 shadow-2xl min-h-[60vh]" data-id="element-1134">
        {createStep === 1 && <div className="animate-in fade-in duration-300" data-id="element-1135">
            <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1136">
              Pick a place
            </h2>
            <div className="relative mb-4" data-id="element-1137">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} data-id="element-1138" />
              <input type="text" placeholder="Search cafes, restaurants..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3.5 rounded-2xl border-none bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black" data-id="element-1139" />
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto" data-id="element-1140">
              {filteredStores.map(store => <button key={store.id} onClick={() => {
            setNewPlanStore(store);
            setCreateStep(2);
          }} className={`w-full bg-white rounded-2xl p-4 flex items-center gap-4 text-left active:scale-[0.98] transition-all border-2 ${newPlanStore?.id === store.id ? 'border-black shadow-md' : 'border-transparent shadow-sm'}`} data-id="element-1141">
                  <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-1142">
                    <img src={store.image} alt={store.name} className="w-full h-full object-cover" data-id="element-1143" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1144">
                    <h3 className="font-bold text-gray-900 text-base mb-1 truncate" data-id="element-1145">
                      {store.name}
                    </h3>
                    <p className="text-xs text-gray-500 truncate" data-id="element-1146">
                      {store.distance} • {store.tags[0]}
                    </p>
                  </div>
                  {newPlanStore?.id === store.id && <Check size={20} className="text-black" data-id="element-1147" />}
                </button>)}
            </div>
          </div>}

        {createStep === 2 && <div className="animate-in fade-in duration-300" data-id="element-1148">
            <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1149">
              Pick a time
            </h2>

            <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-1150">
              {[{
            label: 'Today Evening',
            d: new Date().toISOString().split('T')[0],
            t: '18:00'
          }, {
            label: 'Tomorrow Lunch',
            d: new Date(Date.now() + 86400000).toISOString().split('T')[0],
            t: '13:00'
          }, {
            label: 'Tomorrow Evening',
            d: new Date(Date.now() + 86400000).toISOString().split('T')[0],
            t: '19:00'
          }].map(opt => <button key={opt.label} onClick={() => {
            setNewPlanDate(opt.d);
            setNewPlanTime(opt.t);
          }} className="bg-white py-3 px-4 rounded-xl text-sm font-bold text-gray-800 shadow-sm active:scale-95" data-id="element-1151">
                  {opt.label}
                </button>)}
            </div>

            <div className="space-y-4 mb-8" data-id="element-1152">
              <div data-id="element-1153">
                <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1154">
                  Date
                </label>
                <input type="date" value={newPlanDate} onChange={e => setNewPlanDate(e.target.value)} className="w-full p-4 rounded-2xl border-none bg-white text-gray-900 focus:ring-2 focus:ring-black" data-id="element-1155" />
              </div>
              <div data-id="element-1156">
                <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-1157">
                  Time
                </label>
                <input type="time" value={newPlanTime} onChange={e => setNewPlanTime(e.target.value)} className="w-full p-4 rounded-2xl border-none bg-white text-gray-900 focus:ring-2 focus:ring-black" data-id="element-1158" />
              </div>
            </div>

            <button onClick={() => setCreateStep(3)} disabled={!newPlanDate || !newPlanTime} className="w-full py-4 bg-black text-[#FFFC00] rounded-2xl font-bold text-lg active:scale-[0.98] transition-transform shadow-lg disabled:opacity-50" data-id="element-1159">
              Continue
            </button>
          </div>}

        {createStep === 3 && <div className="animate-in fade-in duration-300" data-id="element-1160">
            <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1161">
              Invite friends
            </h2>

            <div className="space-y-2 max-h-[400px] overflow-y-auto mb-6" data-id="element-1162">
              {mockContacts.map(contact => {
            const isSelected = selectedContacts.includes(contact.id);
            return <button key={contact.id} onClick={() => {
              if (isSelected) setSelectedContacts(selectedContacts.filter(id => id !== contact.id));else setSelectedContacts([...selectedContacts, contact.id]);
            }} className={`w-full bg-white rounded-2xl p-4 flex items-center gap-3 text-left active:scale-[0.98] transition-all border-2 ${isSelected ? 'border-black shadow-md bg-yellow-50' : 'border-transparent shadow-sm'}`} data-id="element-1163">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1164">
                      <User size={20} className="text-gray-600" data-id="element-1165" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1166">
                      <p className="font-bold text-gray-900 text-base truncate" data-id="element-1167">
                        {contact.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1168">{contact.phone}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isSelected ? 'bg-black border-black' : 'border-gray-300'}`} data-id="element-1169">
                      {isSelected && <Check size={14} className="text-white" strokeWidth={3} data-id="element-1170" />}
                    </div>
                  </button>;
          })}
            </div>

            <button onClick={handleCreatePlan} disabled={selectedContacts.length === 0} className="w-full py-4 bg-black text-[#FFFC00] rounded-2xl font-bold text-lg active:scale-[0.98] transition-transform shadow-lg disabled:opacity-50" data-id="element-1171">
              Create Plan ({selectedContacts.length} invited)
            </button>
          </div>}
      </div>
    </div>;
  const renderDetail = () => {
    if (!selectedPlan) return null;
    const isCompleted = selectedPlan.status === 'completed' || selectedPlan.status === 'expired';
    const isLocked = selectedPlan.status === 'locked';
    const isHost = selectedPlan.members.some(m => m.isHost && (m.id === 'me' || m.name === 'You' || m.name === 'Aryan'));
    const totalMembers = selectedPlan.members.length;
    const confirmedCount = selectedPlan.members.filter(m => m.status === 'confirmed').length;
    const threshold = Math.ceil(totalMembers * 0.6);
    return <div className={`pb-24 pt-6 px-5 animate-in fade-in slide-in-from-right duration-300 min-h-screen ${isCompleted ? 'bg-gray-100' : isLocked ? 'bg-green-50' : 'bg-[#FFFC00]'}`} data-id="element-1172">
        <div className="flex items-center gap-4 mb-6" data-id="element-1173">
          <button onClick={() => setView('list')} className="w-10 h-10 bg-black rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-1174">
            <ArrowLeft size={20} className="text-[#FFFC00]" strokeWidth={2.5} data-id="element-1175" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 flex-1 truncate" data-id="element-1176">
            {selectedPlan.title}
          </h1>
          {isCompleted && <span className="bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-full" data-id="element-1177">
              Completed ✓
            </span>}
          {isLocked && !isCompleted && <span className="bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full animate-in zoom-in duration-300" data-id="element-1178">
              Confirmed ✓
            </span>}
        </div>

        {/* Final Plan Confirmed Banner */}
        {isLocked && !isCompleted && <div className="bg-green-500 rounded-2xl p-5 mb-6 shadow-lg animate-in slide-in-from-top duration-500" data-id="element-1179">
            <div className="text-center" data-id="element-1180">
              <div className="text-4xl mb-2" data-id="element-1181">🎉</div>
              <h2 className="text-xl font-bold text-white mb-1" data-id="element-1182">
                Plan Confirmed!
              </h2>
              <p className="text-green-100 text-sm mb-3" data-id="element-1183">
                {confirmedCount} friends are going
              </p>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 inline-block" data-id="element-1184">
                <CountdownDisplay dateStr={selectedPlan.dateTime} data-id="element-1185" />
              </div>
            </div>
          </div>}

        <div className={`bg-white rounded-3xl p-6 shadow-2xl mb-6 ${isCompleted ? 'opacity-80' : ''}`} data-id="element-1186">
          {/* Header Info */}
          <div className="mb-6" data-id="element-1187">
            <div className="flex items-center gap-3 mb-4" data-id="element-1188">
              <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-1189">
                <img src={selectedPlan.storeImage} alt={selectedPlan.storeName} className="w-full h-full object-cover" data-id="element-1190" />
              </div>
              <div data-id="element-1191">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-1192">
                  {selectedPlan.storeName}
                </h2>
                <div className="flex items-center gap-1 text-gray-600 text-sm mt-1" data-id="element-1193">
                  <CalendarIcon size={14} data-id="element-1194" />
                  {new Date(selectedPlan.dateTime).toLocaleString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
                </div>
              </div>
            </div>
            {!isCompleted && !isLocked && <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between border border-gray-100" data-id="element-1195">
                <span className="text-sm font-semibold text-gray-700" data-id="element-1196">
                  Status
                </span>
                <CountdownDisplay dateStr={selectedPlan.dateTime} data-id="element-1197" />
              </div>}
          </div>

          {/* Members Section */}
          <div className="mb-6" data-id="element-1198">
            <div className="flex justify-between items-end mb-3" data-id="element-1199">
              <h3 className="font-bold text-gray-900" data-id="element-1200">Going</h3>
              <span className="text-xs font-bold text-gray-500" data-id="element-1201">
                {confirmedCount} of {totalMembers} confirmed
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-100 rounded-full h-2 mb-4 overflow-hidden" data-id="element-1202">
              <div className={`h-full rounded-full transition-all duration-700 ease-out ${confirmedCount >= threshold ? 'bg-green-500' : 'bg-yellow-400'}`} style={{
              width: `${confirmedCount / totalMembers * 100}%`
            }} data-id="element-1203" />
            </div>

            {/* Threshold indicator */}
            {!isLocked && !isCompleted && <p className="text-xs text-gray-400 mb-3 text-center" data-id="element-1204">
                Need {threshold} confirmations to lock the plan
              </p>}

            <div className="space-y-2" data-id="element-1205">
              {selectedPlan.members.map(member => <div key={member.id} className={`flex items-center justify-between p-2.5 rounded-xl transition-all duration-300 ${member.status === 'confirmed' ? 'bg-green-50' : member.status === 'maybe' ? 'bg-amber-50' : member.status === 'declined' ? 'bg-red-50' : 'bg-gray-50'}`} data-id="element-1206">
                  <div className="flex items-center gap-2" data-id="element-1207">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${member.status === 'confirmed' ? 'bg-green-200 text-green-800' : member.status === 'maybe' ? 'bg-amber-200 text-amber-800' : 'bg-gray-200 text-gray-700'}`} data-id="element-1208">
                      {member.name.charAt(0)}
                    </div>
                    <span className="font-medium text-gray-900" data-id="element-1209">
                      {member.name}
                    </span>
                    {member.isHost && <span className="text-xs" data-id="element-1210">👑</span>}
                  </div>
                  <div className="flex items-center gap-1" data-id="element-1211">
                    {member.status === 'confirmed' && <>
                        <span className="text-xs text-green-600 font-medium mr-1" data-id="element-1212">
                          Going
                        </span>
                        <Check size={16} className="text-green-500" strokeWidth={3} data-id="element-1213" />
                      </>}
                    {member.status === 'maybe' && <span className="text-amber-600 font-bold text-sm px-2 py-0.5 bg-amber-100 rounded-full" data-id="element-1214">
                        Maybe
                      </span>}
                    {member.status === 'declined' && <>
                        <span className="text-xs text-red-600 font-medium mr-1" data-id="element-1215">
                          Can't
                        </span>
                        <X size={16} className="text-red-500" strokeWidth={3} data-id="element-1216" />
                      </>}
                    {member.status === 'pending' && <span className="text-xs text-gray-400 font-medium flex items-center gap-1" data-id="element-1217">
                        <Clock size={14} data-id="element-1218" /> Waiting...
                      </span>}
                  </div>
                </div>)}
            </div>
          </div>

          {/* Host sees: Waiting for responses indicator (only during voting) */}
          {isHost && !isCompleted && !isLocked && <div className="mb-6 pt-4 border-t border-gray-100" data-id="element-1219">
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center" data-id="element-1220">
                <div className="flex items-center justify-center gap-2 mb-2" data-id="element-1221">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" data-id="element-1222" />
                  <span className="text-sm font-bold text-yellow-800" data-id="element-1223">
                    Waiting for responses...
                  </span>
                </div>
                <p className="text-xs text-yellow-700" data-id="element-1224">
                  Share the plan link so friends can respond
                </p>
              </div>
            </div>}

          {/* Non-host sees: Response buttons (only during voting) */}
          {!isHost && !isCompleted && !isLocked && <div className="mb-6 pt-4 border-t border-gray-100" data-id="element-1225">
              <p className="text-sm font-bold text-gray-700 mb-3 text-center" data-id="element-1226">
                Your Response
              </p>
              <div className="flex gap-2" data-id="element-1227">
                <button onClick={() => handleUpdateStatus(selectedPlan.id, 'confirmed')} className="flex-1 py-2.5 bg-green-100 text-green-700 rounded-xl font-bold text-sm active:scale-95 transition-transform" data-id="element-1228">
                  Confirm ✓
                </button>
                <button onClick={() => handleUpdateStatus(selectedPlan.id, 'maybe')} className="flex-1 py-2.5 bg-amber-100 text-amber-700 rounded-xl font-bold text-sm active:scale-95 transition-transform" data-id="element-1229">
                  Maybe ?
                </button>
                <button onClick={() => handleUpdateStatus(selectedPlan.id, 'declined')} className="flex-1 py-2.5 bg-red-100 text-red-700 rounded-xl font-bold text-sm active:scale-95 transition-transform" data-id="element-1230">
                  Can't make it
                </button>
              </div>
            </div>}

          {/* Group Perk */}
          {selectedPlan.groupPerk && !isCompleted && <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 mb-6 animate-in zoom-in duration-300" data-id="element-1231">
              <div className="flex items-center gap-2 mb-1" data-id="element-1232">
                <span className="text-xl" data-id="element-1233">🎁</span>
                <h4 className="font-bold text-purple-900" data-id="element-1234">
                  Group Perk Unlocked!
                </h4>
              </div>
              <p className="text-purple-700 text-sm font-medium" data-id="element-1235">
                {selectedPlan.groupPerk}
              </p>
              <p className="text-purple-500 text-xs mt-1" data-id="element-1236">
                {confirmedCount} friends confirmed
              </p>
            </div>}

          {/* Suggestions - Host sees read-only, non-host sees interactive (only during voting) */}
          {!isCompleted && !isLocked && selectedPlan.suggestions.length > 0 && <div className="mb-6 pt-4 border-t border-gray-100" data-id="element-1237">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-1238">Suggestions</h3>
              {selectedPlan.suggestions.map(sugg => <div key={sugg.id} className="bg-gray-50 rounded-xl p-3 mb-3 border border-gray-200" data-id="element-1239">
                  <p className="text-sm text-gray-600 mb-2" data-id="element-1240">
                    <span className="font-bold text-gray-900" data-id="element-1241">
                      {sugg.authorName}
                    </span>{' '}
                    suggests:
                  </p>
                  <p className="font-bold text-gray-900 mb-3" data-id="element-1242">
                    {sugg.type === 'time' ? new Date(sugg.value).toLocaleString('en-US', {
                weekday: 'short',
                hour: 'numeric',
                minute: '2-digit'
              }) : sugg.value}
                  </p>
                  {!isHost && <div className="flex gap-2" data-id="element-1243">
                      <button className="flex-1 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-700 flex items-center justify-center gap-1 active:bg-gray-50" data-id="element-1244">
                        <ThumbsUp size={14} data-id="element-1245" /> Works ({sugg.votesUp.length})
                      </button>
                      <button className="flex-1 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-700 flex items-center justify-center gap-1 active:bg-gray-50" data-id="element-1246">
                        <ThumbsDown size={14} data-id="element-1247" /> Can't ({sugg.votesDown.length})
                      </button>
                    </div>}
                  {isHost && <div className="flex gap-3 text-xs text-gray-500" data-id="element-1248">
                      <span className="flex items-center gap-1" data-id="element-1249">
                        <ThumbsUp size={12} data-id="element-1250" /> {sugg.votesUp.length} works
                      </span>
                      <span className="flex items-center gap-1" data-id="element-1251">
                        <ThumbsDown size={12} data-id="element-1252" /> {sugg.votesDown.length} can't
                      </span>
                    </div>}
                </div>)}
            </div>}

          {/* Bottom Actions */}
          <div className="space-y-3 pt-4 border-t border-gray-100" data-id="element-1253">
            {!isCompleted ? <>
                <button onClick={() => handleShareWhatsApp(selectedPlan)} className="w-full py-3.5 bg-[#25D366] text-white rounded-xl font-bold text-base active:scale-[0.98] transition-transform shadow-md flex items-center justify-center gap-2" data-id="element-1254">
                  <Share2 size={18} data-id="element-1255" /> Share on WhatsApp
                </button>
                <button onClick={() => showToast('Final plans sent to all friends! 🔔', 'success')} className="w-full py-3.5 bg-black text-[#FFFC00] rounded-xl font-bold text-base active:scale-[0.98] transition-transform shadow-md flex items-center justify-center gap-2" data-id="element-1256">
                  📩 Send Final Plans to Friends
                </button>
              </> : <>
                <button onClick={() => {
              setNewPlanStore(STORES.find(s => s.id === selectedPlan.storeId) || null);
              setView('create');
              setCreateStep(2);
            }} className="w-full py-3.5 bg-black text-[#FFFC00] rounded-xl font-bold text-base active:scale-[0.98] transition-transform shadow-md" data-id="element-1257">
                  Plan Again
                </button>
                <button onClick={() => onNavigate('gifting')} className="w-full py-3.5 bg-white border-2 border-gray-200 text-gray-900 rounded-xl font-bold text-base active:scale-[0.98] transition-transform" data-id="element-1258">
                  Send a Treat
                </button>
              </>}
          </div>
        </div>
      </div>;
  };
  return <>
      {view === 'list' && renderList()}
      {view === 'create' && renderCreate()}
      {view === 'detail' && renderDetail()}
    </>;
}